import { LEGAL_SOURCES, type LegalKind } from './legal-sources';

export type { LegalKind } from './legal-sources';

export type LegalBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'subheading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] };

export type LegalCopy = {
  title: string;
  eyebrow: string;
  backToSettings: string;
  blocks: LegalBlock[];
};

const BACK_TO_SETTINGS: Record<string, string> = {
  'zh-CN': '返回设置',
  'zh-TW': '返回設定',
  'en-US': 'Back to settings',
  'ja-JP': '設定に戻る',
  'es-ES': 'Volver a ajustes',
  'th-TH': 'กลับไปที่การตั้งค่า',
  'id-ID': 'Kembali ke pengaturan',
  'ko-KR': '설정으로 돌아가기',
  'de-DE': 'Zurück zu den Einstellungen',
  'ru-RU': 'Вернуться к настройкам',
  'vi-VN': 'Quay lại cài đặt',
};

const LANGUAGE_ALIASES: Record<string, string> = {
  zh: 'zh-CN',
  'zh-hans': 'zh-CN',
  'zh-cn': 'zh-CN',
  'zh-hant': 'zh-TW',
  'zh-tw': 'zh-TW',
  'zh-hk': 'zh-TW',
  en: 'en-US',
  'en-us': 'en-US',
  ja: 'ja-JP',
  'ja-jp': 'ja-JP',
  es: 'es-ES',
  'es-es': 'es-ES',
  th: 'th-TH',
  'th-th': 'th-TH',
  id: 'id-ID',
  'id-id': 'id-ID',
  ko: 'ko-KR',
  'ko-kr': 'ko-KR',
  de: 'de-DE',
  'de-de': 'de-DE',
  ru: 'ru-RU',
  'ru-ru': 'ru-RU',
  vi: 'vi-VN',
  'vi-vn': 'vi-VN',
};

function parseLegalContent(content: string): LegalBlock[] {
  const blocks: LegalBlock[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push({ kind: 'list', items: listItems });
    listItems = [];
  };

  const lines = content.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const rawLine = lines[lineIndex];
    const line = rawLine.trim();

    if (line.startsWith('# ')) {
      continue;
    } else if (line.startsWith('## ')) {
      flushList();
      blocks.push({ kind: 'heading', text: line.slice(3) });
    } else if (line.startsWith('### ')) {
      // The source contains a few untranslated/empty third-level headings.
      // Keep the official text intact while avoiding a dangling heading in the UI.
      const nextLine = lines[lineIndex + 1];
      if (nextLine?.startsWith('#')) continue;
      flushList();
      blocks.push({ kind: 'subheading', text: line.slice(4) });
    } else if (/^\d+\.\s/.test(line)) {
      listItems.push(line.replace(/^\d+\.\s/, ''));
    } else {
      flushList();
      blocks.push({ kind: 'paragraph', text: line });
    }
  }

  flushList();
  return blocks;
}

export function getLegalCopy(languageCode: string, kind: LegalKind): LegalCopy {
  const normalized = languageCode.replace('_', '-').toLowerCase();
  const requestedLanguage = LANGUAGE_ALIASES[normalized] ?? LANGUAGE_ALIASES[normalized.split('-')[0]] ?? 'en-US';
  const source = (LEGAL_SOURCES[requestedLanguage] ?? LEGAL_SOURCES['en-US'])[kind];

  return {
    title: source.title,
    eyebrow: kind === 'privacy' ? 'LEGAL / PRIVACY' : 'LEGAL / TERMS',
    backToSettings: BACK_TO_SETTINGS[requestedLanguage] ?? BACK_TO_SETTINGS['en-US'],
    blocks: parseLegalContent(source.content),
  };
}
